import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import LatestSdk from '../welcome/LatestSdk';
import NavLink from '../routing/NavLink';
import useKiponosNavigator from '../../hooks/useKiponosNavigator';
import CopyToClip from '../../infra/CopyToClip';
import useLatestSdk from '../../hooks/useLatestSdk';
import imgDBParams from '../../assets/Kiponos-DB-Params.webp';
import DocsShell, { DocsCard, DocsCode, DocsFeature, DocsNote } from './DocsShell';

const SECTIONS = [
  { id: 'overview', label: 'Overview' },
  { id: 'start', label: 'Quickstart' },
  { id: 'sdk', label: 'SDK' }
];

const TOC = {
  overview: [
    { id: 'ov-why', label: 'Why live config' },
    { id: 'ov-ideas', label: 'Core ideas' },
    { id: 'ov-tree', label: 'Hierarchy' },
    { id: 'ov-uses', label: 'Typical uses' }
  ],
  start: [
    { id: 'qs-flow', label: 'Flow' },
    { id: 'qs-hub', label: 'In the hub' },
    { id: 'qs-app', label: 'In your app' }
  ],
  sdk: [
    { id: 'sdk-dep', label: 'Dependency' },
    { id: 'sdk-tokens', label: 'Tokens & path' },
    { id: 'sdk-api', label: 'API essentials' },
    { id: 'sdk-perms', label: 'Permissions' }
  ]
};

const VALID = new Set(SECTIONS.map((s) => s.id));

function sectionFromHash(hash, fallback) {
  const raw = (hash || '').replace(/^#/, '');
  if (!raw) return fallback;
  const head = raw.split('/')[0];
  return VALID.has(head) ? head : fallback;
}

/**
 * Help hub: Overview · Quickstart · SDK
 * Palette stays HelpGold (DocsShell local --help-*); layout free to evolve.
 */
export default function HelpHub({ initialSection }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { PageKeys, navTo } = useKiponosNavigator();
  const { group, artifact, version, loading, fullName } = useLatestSdk();

  const fallbackSection = useMemo(() => {
    if (initialSection && VALID.has(initialSection)) return initialSection;
    if (location.pathname.startsWith('/about')) return 'overview';
    return 'start';
  }, [initialSection, location.pathname]);

  const [section, setSectionState] = useState(() =>
    sectionFromHash(location.hash, fallbackSection)
  );

  const setSection = useCallback(
    (id) => {
      if (!VALID.has(id)) return;
      setSectionState(id);
      const base = location.pathname.startsWith('/about') ? '/about' : '/docs';
      navigate(`${base}#${id}`, { replace: true });
      // scroll help body to top
      requestAnimationFrame(() => {
        document.querySelector('.docsShell-scroll')?.scrollTo?.({ top: 0, behavior: 'smooth' });
      });
    },
    [location.pathname, navigate]
  );

  useEffect(() => {
    const fromHash = sectionFromHash(location.hash, fallbackSection);
    setSectionState(fromHash);
  }, [location.hash, fallbackSection]);

  const gradleCode = `implementation '${loading ? '…' : fullName}'`;
  const mavenCode = `<dependency>
  <groupId>${loading ? '…' : group || 'io.kiponos'}</groupId>
  <artifactId>${loading ? '…' : artifact || 'sdk-boot-3'}</artifactId>
  <version>${loading ? '…' : version || '…'}</version>
</dependency>`;

  const javaRead = `Kiponos kiponos = Kiponos.createForCurrentTeam();
int maxUsernameLength = kiponos.getInt("max-username-length");
String isReadyApi = kiponos.get("is-ready-api");

KiponosFolder db = kiponos.path("Server", "DB", "PostgreSql");
String host = db.get("host");`;

  const javaListen = `kiponos.afterValueChanged(info -> {
    if ("dbHost".equals(info.key)) {
        reconnectPostgres();
    }
});`;

  const meta = {
    overview: {
      title: 'What is Kiponos?',
      subtitle: 'Live configuration for running apps — edit in the hub, SDKs update without redeploy.'
    },
    start: {
      title: 'Quickstart',
      subtitle: 'Account → first key → SDK → read a value. About five minutes.'
    },
    sdk: {
      title: 'SDK reference',
      subtitle: 'Coordinates, tokens, path selector, and the essentials of the Java API.'
    }
  }[section];

  return (
    <DocsShell
      title={meta.title}
      subtitle={meta.subtitle}
      sections={SECTIONS}
      activeSection={section}
      onSection={setSection}
      toc={TOC[section] || null}
    >
      {section === 'overview' && (
        <OverviewSection
          navTo={navTo}
          PageKeys={PageKeys}
          onGoStart={() => setSection('start')}
        />
      )}
      {section === 'start' && (
        <StartSection
          PageKeys={PageKeys}
          gradleCode={gradleCode}
          mavenCode={mavenCode}
          javaRead={javaRead}
          onGoSdk={() => setSection('sdk')}
        />
      )}
      {section === 'sdk' && (
        <SdkSection
          gradleCode={gradleCode}
          mavenCode={mavenCode}
          javaRead={javaRead}
          javaListen={javaListen}
          PageKeys={PageKeys}
          navTo={navTo}
        />
      )}
    </DocsShell>
  );
}

function OverviewSection({ navTo, PageKeys, onGoStart }) {
  return (
    <>
      <DocsCard id='ov-why'>
        <p className='docsShell-lead'>
          Kiponos is a <strong>live config hub</strong>: you change keys online; connected JVMs keep a hot local
          map updated over a permanent push channel. No “save properties + restart” for ordinary value changes.
        </p>
        <div className='docsShell-heroStrip'>
          <span className='docsShell-pill'>Field-level commit</span>
          <span className='docsShell-pill'>In-memory SDK reads</span>
          <span className='docsShell-pill'>Team · role · env</span>
        </div>
        <div className='docsShell-actions' style={{ marginTop: 16 }}>
          <button type='button' className='docsShell-btn primary' onClick={onGoStart}>
            Start in 5 minutes
          </button>
          <button
            type='button'
            className='docsShell-btn secondary'
            onClick={() => navTo(PageKeys.CONFIGS_HIDE_SDK || PageKeys.CONFIGS_SHOW_SDK)}
          >
            Open Configs
          </button>
        </div>
      </DocsCard>

      <DocsCard id='ov-ideas' title='Core ideas'>
        <div className='docsShell-grid'>
          <DocsFeature title='Push, don’t poll'>
            Deltas land in the SDK as you edit. <code>get</code> hits local memory.
          </DocsFeature>
          <DocsFeature title='Tree, not a bag of keys'>
            Folders and items — like a small filesystem for config.
          </DocsFeature>
          <DocsFeature title='Scoped access'>
            Rights follow team + role + selected environment. Multi-team users supported.
          </DocsFeature>
          <DocsFeature title='Unique path'>
            Application → Release → Environment → profile (e.g. <code>base</code>, <code>SSL</code>).
          </DocsFeature>
        </div>
      </DocsCard>

      <DocsCard id='ov-tree' title='Hierarchy'>
        <div className='docsShell-tree'>{`Team
└── Application
    └── Release
        └── Environment          e.g. dev · staging · prod
            └── Config profile   e.g. base · tests · SSL`}</div>
        <div className='docsShell-figure'>
          <img src={imgDBParams} alt='Example config tree in the Kiponos hub' />
        </div>
      </DocsCard>

      <DocsCard id='ov-uses' title='Typical uses'>
        <ul>
          <li>Log levels and feature flags in production without restart</li>
          <li>Rotate secrets / endpoints while processes stay up</li>
          <li>Per-env overrides without shipping a new artifact</li>
          <li>Kill-switches and gradual rollouts controlled from the hub</li>
        </ul>
        <DocsNote>
          Keep Help content focused on the hub + Java SDK path. Product tasks (Task Mgr, games) stay out of
          this guide.
        </DocsNote>
        <div className='docsShell-actions'>
          <button type='button' className='docsShell-btn primary' onClick={onGoStart}>
            Quickstart
          </button>
          <button type='button' className='docsShell-btn secondary' onClick={() => navTo(PageKeys.SIGNUP)}>
            Sign up
          </button>
        </div>
      </DocsCard>
    </>
  );
}

function StartSection({ PageKeys, gradleCode, mavenCode, javaRead, onGoSdk }) {
  return (
    <>
      <DocsCard id='qs-flow' title="What you'll do">
        <ol className='docsShell-steps'>
          <li>
            <strong>Hub</strong>
            Sign up, open the default <code>dev</code> / <code>base</code> tree, add one key + value.
          </li>
          <li>
            <strong>Build</strong>
            Add the Java SDK from Maven Central (latest: <LatestSdk />).
          </li>
          <li>
            <strong>Auth</strong>
            Generate env tokens under <strong>Connect Your App</strong>; set{' '}
            <code>KIPONOS_ID</code> and <code>KIPONOS_ACCESS</code>.
          </li>
          <li>
            <strong>Code</strong>
            <code>Kiponos.createForCurrentTeam()</code> then <code>get</code> / <code>getInt</code>.
          </li>
        </ol>
      </DocsCard>

      <DocsCard id='qs-hub' title='In the hub'>
        <p>
          After
          <span className='docsShell-inlineNav'>
            <NavLink pageKey={PageKeys.SIGNUP} withActiveStyle={false} />
          </span>
          /
          <span className='docsShell-inlineNav'>
            <NavLink pageKey={PageKeys.LOGIN} withActiveStyle={false} />
          </span>
          you already have <code>dev</code> + <code>base</code>. Use the wizard to select them, then{' '}
          <strong>Configs</strong> → add item → key (e.g. <code>max-retries</code>) → value. No separate Save —
          each field commits live.
        </p>
        <DocsNote>
          Teammates with access see new keys immediately. Roles control who can write.
        </DocsNote>
      </DocsCard>

      <DocsCard id='qs-app' title='In your app'>
        <DocsCode label='Gradle' text={gradleCode} copySlot={<CopyToClip text={gradleCode} />} />
        <DocsCode label='Maven' text={mavenCode} copySlot={<CopyToClip text={mavenCode} />} />
        <p className='docsShell-muted' style={{ marginTop: 12 }}>
          Tokens: <strong>Connect Your App</strong> → generate for the environment → copy into process env.
        </p>
        <DocsCode label='Java' text={javaRead} copySlot={<CopyToClip text={javaRead} />} />
        <DocsNote>
          Optional profile selector:{' '}
          <code>-Dkiponos=&quot;['App']['1.0.0']['dev']['base']&quot;</code> — full path from Connect Your App.
          Default is environment <code>base</code>.
        </DocsNote>
        <div className='docsShell-actions'>
          <button type='button' className='docsShell-btn secondary' onClick={onGoSdk}>
            More SDK detail
          </button>
        </div>
      </DocsCard>
    </>
  );
}

function SdkSection({ gradleCode, mavenCode, javaRead, javaListen, PageKeys, navTo }) {
  return (
    <>
      <DocsCard id='sdk-dep' title='Dependency'>
        <p>
          Artifact on Maven Central. Live coordinates: <LatestSdk />
        </p>
        <DocsCode label='Gradle' text={gradleCode} copySlot={<CopyToClip text={gradleCode} />} />
        <DocsCode label='Maven' text={mavenCode} copySlot={<CopyToClip text={mavenCode} />} />
        <p>
          <a
            className='docsShell-ext'
            href='https://central.sonatype.com/artifact/io.kiponos/sdk-boot-3'
            target='_blank'
            rel='noopener noreferrer'
          >
            Maven Central → io.kiponos/sdk-boot-3
          </a>
        </p>
      </DocsCard>

      <DocsCard id='sdk-tokens' title='Tokens & path'>
        <ul>
          <li>
            <code>KIPONOS_ID</code> + <code>KIPONOS_ACCESS</code> — per environment (treat as secrets)
          </li>
          <li>
            Create/copy under <strong>Connect Your App</strong>
          </li>
          <li>
            Optional JVM: <code>-Dkiponos=&quot;['App']['rel']['env']['profile']&quot;</code> — case-sensitive
          </li>
        </ul>
        <DocsNote danger>
          <strong>Do not commit tokens.</strong> Same env tokens can be shared by multiple apps in that environment.
        </DocsNote>
      </DocsCard>

      <DocsCard id='sdk-api' title='API essentials'>
        <h3>Read & navigate</h3>
        <DocsCode label='Java' text={javaRead} copySlot={<CopyToClip text={javaRead} />} />
        <h3>Listen</h3>
        <DocsCode label='Java' text={javaListen} copySlot={<CopyToClip text={javaListen} />} />
        <h3>Write (when your role allows)</h3>
        <DocsCode
          label='Java'
          text={`sdk.set("max-queue-size", "5000");
sdk.createFolder("new-folder");
sdk.folder("rabbit-mq").set("topic-path", "/topic/notifications");`}
          copySlot={
            <CopyToClip
              text={`sdk.set("max-queue-size", "5000");
sdk.createFolder("new-folder");
sdk.folder("rabbit-mq").set("topic-path", "/topic/notifications");`}
            />
          }
        />
        <p className='docsShell-muted'>
          Creating the client connects to the hub (and reconnects). No server URL in app code — tokens carry
          identity.
        </p>
      </DocsCard>

      <DocsCard id='sdk-perms' title='Permissions (short)'>
        <p>
          Access = <strong>role × team × environment</strong>. Example: Admin on R&amp;D, read-only on QA — not one
          global switch.
        </p>
        <div className='docsShell-actions'>
          <button type='button' className='docsShell-btn primary' onClick={() => navTo(PageKeys.CONFIGS_SHOW_SDK)}>
            Connect Your App
          </button>
          <button type='button' className='docsShell-btn secondary' onClick={() => navTo(PageKeys.SIGNUP)}>
            Sign up
          </button>
        </div>
      </DocsCard>
    </>
  );
}
